// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { forwardRef, type InputHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label:      string
  description?: string
  error?:     string
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, error, className, id, ...props }, ref) => {
    const checkId = id ?? label.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={checkId} className="flex items-start gap-3 cursor-pointer group">
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              ref={ref}
              id={checkId}
              type="checkbox"
              className={cn(
                'peer h-5 w-5 appearance-none rounded-md border-2 bg-white',
                'transition-all duration-150 cursor-pointer',
                'checked:bg-moss checked:border-moss',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 focus-visible:ring-offset-1',
                error ? 'border-status-alertBorder' : 'border-sand-300 group-hover:border-sage-400',
                className,
              )}
              {...props}
            />
            <svg
              className="pointer-events-none absolute inset-0 h-5 w-5 opacity-0 peer-checked:opacity-100 text-white"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                d="M3.5 8L6.5 11L12.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span className="text-sm text-ink-2 leading-relaxed">
            {label}
            {description && (
              <span className="block mt-0.5 text-xs text-ink-4">{description}</span>
            )}
          </span>
        </label>
        {error && (
          <p className="ml-8 text-xs text-status-alertText" role="alert">{error}</p>
        )}
      </div>
    )
  },
)

Checkbox.displayName = 'Checkbox'
